import { JobStatus } from '../../core/batches/entities/batch-object.entity';
import { ApiJob } from '../../core/jobs/entities/api-job.entity';
import { SortDirection } from '../../overviews/generic-overview/components/display-settings-bar/entities/sort-direction.entity';
import { sortByProperty } from '../../overviews/generic-overview/components/display-settings-bar/helpers/sort-by-property.helper';
import { ProcessingTask } from './processing-task.entity';

export enum ProcessingStatus {
    WAITING = 'waiting',
    IN_PROGRESS = 'in-progress',
    INTERRUPTED = 'interrupted',
    WARNING = 'warning',
    FAILED = 'failed',
    COMPLETED = 'completed',
    SKIPPED = 'skipped'
}

export class ProcessingStep {
    readonly #name: string;
    readonly #optional: boolean;
    #status: ProcessingStatus;

    readonly #processingTasks: ProcessingTask[];

    #creationDate: string;
    #startDate: string | null = null;
    #finishDate: string | null = null;

    constructor(name: string, tasks: ProcessingTask[], optionalStep = false) {
        this.#name = name;
        this.#processingTasks = tasks;
        this.#optional = optionalStep;
        this.#status = ProcessingStatus.WAITING;
    }

    get name(): string {
        return this.#name;
    }

    get optional(): boolean {
        return this.#optional;
    }

    get required(): boolean {
        return !this.#optional;
    }

    get status(): ProcessingStatus {
        return this.#status;
    }

    set status(status: ProcessingStatus) {
        this.#status = status;
    }

    get creationDate(): string {
        return this.#creationDate;
    }

    get startDate(): string | null {
        return this.#startDate;
    }

    get finishDate(): string | null {
        return this.#finishDate;
    }

    get labelClass(): string {
        switch (this.#status) {
            case ProcessingStatus.FAILED:
                return 'sh-progress-steps__step__label--error';

            case ProcessingStatus.IN_PROGRESS:
                return 'sh-progress-steps__step__label--in-progress';

            default:
                return 'sh-progress-steps__step__label';
        }
    }

    get statusClass(): string {
        switch (this.#status) {
            case ProcessingStatus.COMPLETED:
                return 'sh-progress-steps__step__status--success';

            case ProcessingStatus.FAILED:
                return 'sh-progress-steps__step__status--error';

            case ProcessingStatus.IN_PROGRESS:
                return 'sh-progress-steps__step__status--in-progress';

            case ProcessingStatus.WARNING:
                return 'sh-progress-steps__step__status--warning';

            default:
                return 'sh-progress-steps__step__status';
        }
    }

    setJobs(jobs: ApiJob[]): void {
        const jobsOfStep = jobs.filter(job => this.isJobOfStep(job));

        this.#creationDate = this.getFirstOfProperty(jobsOfStep, 'CreationDate') ?? new Date().toISOString();
        this.#startDate = this.getFirstOfProperty(jobsOfStep, 'StartDate');
        this.#finishDate = this.getFirstOfProperty(jobsOfStep, 'FinishDate');

        this.calculateStatusFromJobs(jobsOfStep);
    }

    private getFirstOfProperty(jobsOfStep: ApiJob[], property: keyof ApiJob): string | null {
        if (jobsOfStep.length === 0) {
            return null;
        }

        const sortedJobsByProperty: ApiJob[] = sortByProperty(jobsOfStep, property, SortDirection.ASC);

        return sortedJobsByProperty[0][property]?.toString();
    }

    private calculateStatusFromJobs(jobsOfStep: ApiJob[]): void {
        const combinedStatus: ProcessingStatus = this.getStepStatusFromJobs(jobsOfStep);

        this.#status = combinedStatus;
    }

    private isJobOfStep(job: ApiJob): boolean {
        return this.#processingTasks.includes(job.TaskName as ProcessingTask);
    }

    private getStepStatusFromJobs(jobs: ApiJob[]): ProcessingStatus {
        const hasFailed: boolean = jobs.some(
            job => job.Status === JobStatus.FAILED || job.Status === JobStatus.REJECTED
        );

        if (this.#optional) {
            if (jobs.length === 0) {
                return ProcessingStatus.SKIPPED;
            }
            if (hasFailed) {
                return ProcessingStatus.WARNING;
            }
        }

        if (jobs.length === 0) {
            return ProcessingStatus.WAITING;
        }

        const allCompleted: boolean = jobs.every(job => job.Status === JobStatus.COMPLETED);

        if (allCompleted) {
            return ProcessingStatus.COMPLETED;
        }

        if (hasFailed) {
            return ProcessingStatus.FAILED;
        }

        const hasInterrupted: boolean = jobs.some(job => job.Status === JobStatus.INTERRUPTED);
        if (hasInterrupted) {
            return ProcessingStatus.INTERRUPTED;
        }

        const hasProcessing: boolean = jobs.some(
            job => job.Status === JobStatus.IN_PROGRESS || job.Status === JobStatus.PROCESSING
        );

        if (hasProcessing) {
            return ProcessingStatus.IN_PROGRESS;
        }

        // Default to waiting
        return ProcessingStatus.WAITING;
    }
}
